import { Link } from "react-router-dom";
import { useSession } from "../lib/useSession";

// Route-level guard: wraps Pro-only pages. Free accounts get a
// hard-locked upgrade screen instead of the actual page — no
// "Not now" dismiss, no peeking at the feature shell. They can
// either upgrade or hit Back.
//
// Use in App.tsx:
//
//   <Route path="/app/calendar" element={<RequirePro><Calendar /></RequirePro>} />
//
// The backend still enforces with requirePro() middleware (defense in
// depth) — this is purely UX so Free users see the right thing on
// screen without their data fetches 402'ing in the background.

export function RequirePro({ children }: { children: React.ReactNode }) {
  const { user, loading } = useSession();
  if (loading) {
    return <div className="p-6 text-sm text-navy-500">Loading…</div>;
  }
  // No user (logged out) — let the inner page or its own auth guard
  // handle redirect. We only block on plan, not on auth.
  if (!user) return <>{children}</>;
  // Pro (or comped Pro): pass through.
  if (user.account_plan === "pro") return <>{children}</>;

  // Free: render the upgrade screen instead of the actual page.
  return <ProUpgradeScreen />;
}

function ProUpgradeScreen() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="rounded-2xl border border-brand-200 bg-gradient-to-b from-brand-50/40 to-white p-8 shadow-sm">
        <div className="text-5xl mb-3">🔒</div>
        <h1 className="text-2xl font-bold text-navy-900">This is a Pro feature</h1>
        <p className="text-sm text-navy-700 mt-2">
          You're on the Free plan. Upgrade to Pro for $49.99/mo to unlock this
          and everything else FieldKrew has to offer.
        </p>

        <h2 className="text-sm font-semibold text-navy-500 uppercase tracking-wide mt-6 mb-2">
          What you get with Pro
        </h2>
        <ul className="space-y-2 text-sm">
          {[
            "QuickBooks Online — live two-way sync for customers, invoices, and the chart of accounts",
            "Photo documentation — 15 GB included (Free: no image storage)",
            "Voice recording on jobs (hands-free notes for the crew)",
            "Job plan / blueprint uploads",
            "Subcontractors on jobs + sub bidding + payouts",
            "Recurring jobs (auto-spawn on a schedule)",
            "Time off tracking + employee approvals",
            "Custom website builder + your-own-domain",
            "Automatic lead capture (Angi, HomeAdvisor, Thumbtack inbox forwarding)",
            "Automated outreach (seasonal, reactivation, review requests)",
            "Automated follow-up reminders",
            "Team members + role-based permissions (Free is single-user)",
            "200 emails / day through FieldKrew (Free: 50 / month) — or connect your own Resend for unlimited",
            "Card payments via Stripe (2.9% + $0.30 per transaction, same on every CRM) — FieldKrew adds 0% markup on Pro (Free is 1% markup)",
            "AI assistant — chat about your business data",
          ].map((line) => (
            <li key={line} className="flex gap-2">
              <span className="text-brand-500 mt-0.5">✓</span>
              <span>{line}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-navy-500 mt-3">
          Free still gets the core: leads, jobs, customers, invoices,
          calendar, quotes, parts, expenses, reports — all unlimited.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/subscribe"
            className="inline-flex items-center justify-center rounded-lg bg-brand-500 text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand-600"
          >
            Upgrade to Pro — $49.99/mo
          </Link>
          <Link
            to="/app"
            className="inline-flex items-center justify-center rounded-lg border border-navy-300 text-navy-900 px-5 py-2.5 text-sm font-semibold hover:bg-navy-50"
          >
            Back to dashboard
          </Link>
        </div>
        <p className="text-xs text-navy-500 mt-4">
          Cancel any time from Settings. No long-term contracts.
        </p>
      </div>
    </div>
  );
}
